import React, { useState, useEffect, useCallback } from "react";
import { Button, Card, CardBody, Alert, Input, InputGroup, InputGroupText, Badge } from "reactstrap";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useHistory, useParams } from "react-router-dom";
import Loading from "../../components/Loading";
import { fetchPointConfig, fetchPointConfigQRCode } from "../../utils/apiService";

const ViewPointConfigComponent = () => {
  const { brandId, productId, configId } = useParams();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [config, setConfig] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const redemptionUrl = config
    ? `https://api.rankfans.com/redeem/${config.brandId}/${config.id}`
    : '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const [data, qrUrl] = await Promise.all([
        fetchPointConfig(configId, token),
        fetchPointConfigQRCode(configId, token),
      ]);
      setConfig({
        id: data.Id || data.id || 0,
        name: data.name || '',
        value: data.value || 0,
        brandId: data.brandId || 0,
        productId: data.productId || 0,
      });
      setQrCodeUrl(qrUrl);
    } catch (err) {
      setError(err?.message || 'Failed to load point config');
    } finally {
      setLoading(false);
    }
  }, [configId, getAccessTokenSilently]);

  useEffect(() => {
    load();
    return () => {
      if (qrCodeUrl) URL.revokeObjectURL(qrCodeUrl);
    };
  }, [load]);

  const handleCopy = () => {
    navigator.clipboard.writeText(redemptionUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button color="link" className="p-0 me-1" onClick={() => history.push('/brands')}>Brands</Button>
          <span className="text-muted"> &gt; </span>
          <Button color="link" className="p-0 mx-1" onClick={() => history.push(`/brands/${brandId}/products`)}>Products</Button>
          <span className="text-muted"> &gt; </span>
          <Button color="link" className="p-0 mx-1" onClick={() => history.push(`/brands/${brandId}/products/${productId}/configs`)}>Point Configs</Button>
          <span className="text-muted"> &gt; View</span>
        </div>
      </div>

      {error && <Alert color="danger">{error}</Alert>}

      {loading ? (
        <Card>
          <CardBody className="text-center">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 mb-0">Loading...</p>
          </CardBody>
        </Card>
      ) : config ? (
        <Card>
          <CardBody>
            <h5 className="mb-1">{config.name}</h5>
            <p className="text-muted mb-3">
              <Badge color="primary" className="me-2">{config.value} pts</Badge>
              <Badge color="secondary">ID: {config.id}</Badge>
            </p>

            {qrCodeUrl && (
              <div className="text-center mb-4">
                <img src={qrCodeUrl} alt="QR Code" style={{ maxWidth: 200, border: '1px solid #dee2e6', borderRadius: 4 }} />
              </div>
            )}

            <label className="form-label fw-semibold">Redemption URL</label>
            <InputGroup>
              <Input readOnly value={redemptionUrl} />
              <Button color={copied ? 'success' : 'secondary'} onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </InputGroup>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
};

export default withAuthenticationRequired(ViewPointConfigComponent, {
  onRedirecting: () => <Loading />,
});
